import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  PhoneCall, PhoneOff, Clock, Coins, XCircle, CalendarCheck, CalendarClock,
  AlertTriangle, CalendarDays, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

const BUCKETS = [
  { value: "overdue", label: "Прострочені", icon: AlertTriangle, color: "text-rose-700 bg-rose-50" },
  { value: "today", label: "На сьогодні", icon: CalendarCheck, color: "text-emerald-700 bg-emerald-50" },
  { value: "tomorrow", label: "На завтра", icon: CalendarDays, color: "text-sky-700 bg-sky-50" },
  { value: "future", label: "Майбутні", icon: CalendarClock, color: "text-slate-700 bg-slate-100" },
];

const LIFECYCLE_BADGE = {
  new: "bg-slate-100 text-slate-700",
  thinking: "bg-amber-50 text-amber-700",
  collecting: "bg-orange-50 text-orange-700",
  callback: "bg-sky-50 text-sky-700",
  scheduled: "bg-violet-50 text-violet-700",
  operated: "bg-emerald-50 text-emerald-700",
  refused: "bg-rose-50 text-rose-700",
  irrelevant: "bg-slate-100 text-slate-500",
};

const QUICK_ACTIONS = [
  { action: "called_back", label: "Передзвонив", icon: PhoneCall, color: "bg-emerald-600 hover:bg-emerald-700" },
  { action: "no_answer", label: "Не відповідає", icon: PhoneOff, color: "bg-slate-500 hover:bg-slate-600" },
  { action: "thinking", label: "Думає", icon: Clock, color: "bg-amber-500 hover:bg-amber-600" },
  { action: "collecting", label: "Збирає кошти", icon: Coins, color: "bg-orange-500 hover:bg-orange-600" },
  { action: "refused", label: "Відмовився", icon: XCircle, color: "bg-rose-600 hover:bg-rose-700" },
  { action: "scheduled", label: "Записаний", icon: CalendarCheck, color: "bg-violet-600 hover:bg-violet-700" },
];

export default function Calls() {
  const [bucket, setBucket] = useState("today");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ overdue: 0, today: 0, tomorrow: 0, future: 0 });
  const [busy, setBusy] = useState(false);
  const [rescheduleFor, setRescheduleFor] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({ new_call_date: "", comment: "" });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [list, ct] = await Promise.all([
        api.get(`/calls?bucket=${bucket}`),
        api.get("/calls/counts"),
      ]);
      setItems(list.data || []);
      setCounts(ct.data || {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Помилка"); }
    setBusy(false);
  }, [bucket]);
  useEffect(() => { load(); }, [load]);

  const doAction = async (rec, action) => {
    try {
      await api.post(`/recommendations/${rec.id}/call-action`, { action });
      toast.success("Збережено");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitReschedule = async () => {
    if (!rescheduleData.new_call_date) { toast.error("Вкажіть нову дату дзвінка"); return; }
    try {
      await api.post(`/recommendations/${rescheduleFor.id}/call-action`, {
        action: "reschedule",
        new_call_date: rescheduleData.new_call_date,
        comment: rescheduleData.comment,
      });
      toast.success("Дзвінок перенесено");
      setRescheduleFor(null);
      setRescheduleData({ new_call_date: "", comment: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="calls-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-3xl font-bold text-[#0F2027]">Дзвінки</h1>
          <p className="text-sm text-slate-500">Менеджерська черга — рекомендації, що очікують контакту</p>
        </div>
        <Button variant="ghost" size="sm" onClick={load}><RotateCcw className="h-4 w-4 mr-1" />Оновити</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {BUCKETS.map((b) => {
          const Icon = b.icon;
          const isActive = bucket === b.value;
          return (
            <button
              key={b.value}
              onClick={() => setBucket(b.value)}
              data-testid={`bucket-${b.value}`}
              className={`text-left rounded-xl border transition-all p-4 ${isActive ? "border-[#0D9488] ring-2 ring-[#0D9488]/30" : "border-slate-200 hover:border-slate-300"}`}
            >
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${b.color}`}><Icon className="h-4 w-4" /></span>
                <span className="text-2xl font-bold text-[#0F2027]">{counts[b.value] ?? 0}</span>
              </div>
              <div className="mt-2 text-sm font-medium text-[#0F2027]">{b.label}</div>
            </button>
          );
        })}
      </div>

      <Card className="gv-card"><CardContent className="p-0">
        <div className="p-4 border-b border-slate-200 text-sm text-slate-600">
          Список — {BUCKETS.find((b) => b.value === bucket)?.label} ({items.length})
        </div>
        {busy ? <div className="p-8 text-center text-slate-500">Завантаження…</div> :
          items.length === 0 ? <div className="p-8 text-center text-slate-500">Дзвінків немає.</div> :
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
                <tr>
                  <th className="text-left p-3">Пацієнт</th>
                  <th className="text-left p-3">Телефон</th>
                  <th className="text-left p-3">Процедура</th>
                  <th className="text-left p-3">Дата</th>
                  <th className="text-left p-3">Менеджер</th>
                  <th className="text-left p-3">Статус</th>
                  <th className="text-left p-3">Останній коментар</th>
                  <th className="text-right p-3">Дії</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`call-row-${r.id}`}>
                    <td className="p-3"><Link to={`/patients/${r.patient_id}`} className="text-[#0D9488] hover:underline">{r.patient_name || "—"}</Link></td>
                    <td className="p-3 whitespace-nowrap">{r.patient_phone || "—"}</td>
                    <td className="p-3">{r.operation_type} <span className="text-xs text-slate-500">({r.eye})</span></td>
                    <td className="p-3 whitespace-nowrap font-mono text-xs">{r.call_date}</td>
                    <td className="p-3">{r.manager_name || "—"}</td>
                    <td className="p-3"><Badge className={`${LIFECYCLE_BADGE[r.lifecycle_status] || "bg-slate-100 text-slate-700"} hover:${LIFECYCLE_BADGE[r.lifecycle_status]}`}>{r.lifecycle_label}</Badge></td>
                    <td className="p-3 text-slate-600 max-w-[260px] truncate" title={r.last_comment}>{r.last_comment || "—"}</td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-end flex-wrap">
                        {QUICK_ACTIONS.map((a) => {
                          const Icon = a.icon;
                          return (
                            <Button key={a.action} size="sm" onClick={() => doAction(r, a.action)}
                                    data-testid={`act-${a.action}-${r.id}`}
                                    className={`${a.color} text-white h-7 px-2 text-xs`}>
                              <Icon className="h-3 w-3 mr-1" />{a.label}
                            </Button>
                          );
                        })}
                        <Button size="sm" variant="outline" onClick={() => { setRescheduleFor(r); setRescheduleData({ new_call_date: r.call_date, comment: "" }); }}
                                data-testid={`act-reschedule-${r.id}`}
                                className="h-7 px-2 text-xs">
                          <CalendarClock className="h-3 w-3 mr-1" />Перенести
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </CardContent></Card>

      <Dialog open={!!rescheduleFor} onOpenChange={(v) => !v && setRescheduleFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Перенести дзвінок</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600">{rescheduleFor?.patient_name} · {rescheduleFor?.operation_type}</div>
            <div>
              <Label>Нова дата дзвінка</Label>
              <Input type="date" data-testid="resched-date" value={rescheduleData.new_call_date} onChange={(e) => setRescheduleData({ ...rescheduleData, new_call_date: e.target.value })} />
            </div>
            <div>
              <Label>Коментар</Label>
              <Input data-testid="resched-comment" value={rescheduleData.comment} onChange={(e) => setRescheduleData({ ...rescheduleData, comment: e.target.value })} placeholder="(опц.) причина перенесення" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRescheduleFor(null)}>Скасувати</Button>
            <Button onClick={submitReschedule} className="bg-[#0D9488]" data-testid="resched-save">Зберегти</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
